import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { messagesApi } from "@/api/messaging";
import type { MessageAttachmentDto } from "@/api/messaging";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/design-system/lib/utils";
import {
  RESOURCE_PICKERS,
  REGISTERED_PICKER_TYPES,
} from "@/components/messaging/resourcePickers";

/**
 * Internal Messaging Integration Standard v1.1 §6.3 — composer's Attach
 * modal. Replaces the broker-only modal that shipped with v1.0. Step 1 is
 * a type dropdown filtered to the intersection of:
 *   - the messaging service's <c>ResourceTypes.All</c> allow-list, and
 *   - resource types with a registered SPA picker (<c>RESOURCE_PICKERS</c>).
 *
 * Step 2 is the per-type search-and-pick list. Selection drafts an
 * optimistic chip; the server re-resolves the label authoritatively on
 * send.
 */
export function AttachResourceModal({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (attachment: MessageAttachmentDto) => void;
}) {
  // The allow-list is cached at the app shell — every component that needs it
  // re-uses the same react-query entry.
  const allowList = useQuery({
    queryKey: ["messaging", "resource-types"],
    queryFn: () => messagesApi.resourceTypes(),
    staleTime: 60 * 60 * 1000,
    enabled: open,
  });

  // Intersection: the server says these types are chip-able AND the SPA has
  // a picker for them. Order respects REGISTERED_PICKER_TYPES.
  const availableTypes = useMemo(() => {
    const allow = new Set((allowList.data ?? []).map((t) => t.toLowerCase()));
    return REGISTERED_PICKER_TYPES.filter((t) => allow.has(t));
  }, [allowList.data]);

  const [type, setType] = useState<string>("");
  const [q, setQ] = useState("");

  // Default the dropdown to the first available type once the allow-list
  // resolves, and reset on close so the next open is clean.
  useEffect(() => {
    if (!open) {
      setType("");
      setQ("");
      return;
    }
    if (!type && availableTypes.length > 0) setType(availableTypes[0]);
  }, [open, availableTypes, type]);

  const adapter = type ? RESOURCE_PICKERS[type] : undefined;

  const rows = useQuery({
    queryKey: ["messaging", "attach-picker", type, q],
    queryFn: () => adapter!.search(q),
    enabled: open && !!adapter,
    staleTime: 30_000,
  });

  return (
    <Modal open={open} onClose={onClose} dismissOnBackdropClick={false} title="Attach a record" size="md">
      {availableTypes.length === 0 ? (
        <div className="text-small text-gray-mid p-space-3">
          {allowList.isLoading
            ? "Loading…"
            : "No record types available to attach."}
        </div>
      ) : (
        <>
          <div className="grid gap-space-3 sm:grid-cols-[160px_1fr] items-end">
            <label className="grid gap-space-1">
              <span className="text-label uppercase tracking-label text-gray-mid">
                Record type
              </span>
              <select
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setQ("");
                }}
                className={cn(
                  "h-10 px-space-2 rounded-md font-body text-small",
                  "bg-white-pure text-onyx border border-border-light",
                  "focus:outline-none focus:border-gold focus:shadow-goldRing",
                )}
              >
                {availableTypes.map((t) => (
                  <option key={t} value={t}>
                    {RESOURCE_PICKERS[t].typeLabel}
                  </option>
                ))}
              </select>
            </label>
            <div className="relative">
              <Search
                size={16}
                strokeWidth={1.5}
                className="absolute left-space-3 top-1/2 -translate-y-1/2 text-gray-mid"
                aria-hidden
              />
              <input
                autoFocus
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={adapter?.placeholder ?? "Search…"}
                aria-label="Search records"
                className={cn(
                  "w-full h-10 pl-10 pr-space-3 rounded-md font-body text-small",
                  "bg-white-pure text-onyx border border-border-light",
                  "focus:outline-none focus:border-gold focus:shadow-goldRing",
                )}
              />
            </div>
          </div>

          <ul className="mt-space-3 m-0 p-0 list-none max-h-[50vh] overflow-y-auto">
            {rows.isLoading && (
              <li className="text-small text-gray-mid p-space-3">Searching…</li>
            )}
            {!rows.isLoading && (rows.data?.length ?? 0) === 0 && (
              <li className="text-small text-gray-mid p-space-3">
                No {adapter?.typeLabel.toLowerCase() ?? "records"} found.
              </li>
            )}
            {rows.data?.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => {
                    onPick(row.toAttachment());
                    onClose();
                  }}
                  className={cn(
                    "w-full text-left px-space-3 py-space-2 rounded-md",
                    "hover:bg-white-warm focus-visible:outline-none focus-visible:bg-white-warm",
                  )}
                >
                  <div className="font-display font-semibold text-small text-onyx">
                    {row.primary}
                  </div>
                  {row.secondary && (
                    <div className="text-label text-gray-mid">{row.secondary}</div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}
